import { Component, OnInit, Input, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { ReservationService } from 'src/app/shared/services/rest/reservation.service';
import { MessageService } from 'src/app/shared/services/message.service';
import { MessageType } from 'src/app/shared/models/message';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Reservation } from 'src/app/shared/models/reservation';
import { StatusId } from 'src/app/shared/models/status-id';
import { TranslateService } from '@ngx-translate/core';
import { ResourceType } from 'src/app/shared/models/resource-type';

@Component({
  selector: 'app-confirm-reservation-dialog',
  templateUrl: './confirm-reservation-dialog.component.html',
  styleUrls: ['./confirm-reservation-dialog.component.scss']
})
export class ConfirmReservationDialogComponent implements OnInit {

  /**
   * Id of the reservation to display (idAnlass)
   *
   * @type {number}
   * @memberof ConfirmReservationDialogComponent
   */
  @Input() idReservation: number;

  /**
   * The current reservation object to display
   *
   * @type {Reservation}
   * @memberof ConfirmReservationDialogComponent
   */
  reservation: Reservation;

  /**
   * show loading cycle
   *
   * @type {boolean}
   * @memberof ConfirmReservationDialogComponent
   */
  isLoading = false;

  /**
   * show loading cycle on ics-download button
   *
   * @memberof ConfirmReservationDialogComponent
   */
  isButtonLoading = false;

  viewData: IViewData;

  constructor(
    private reservationService: ReservationService,
    private messages: MessageService,
    private translate: TranslateService) {
    this.viewData = {
      title: '',
      mainText: '',
      subText: ''
    };
  }

  ngOnInit() {
    this.loadData();
  }

  /**
   * Load the reservation with idReservation from server
   *
   * @memberof ConfirmReservationDialogComponent
   */
  loadData() {
    this.isLoading = true;
    this.reservationService.get(String(this.idReservation)).toPromise()
      .then(
        (data: Reservation) => {
          this.reservation = (data instanceof HttpResponse) ? Reservation.create(data.body) : Reservation.create(data);
          this.initStatus();

          this.isLoading = false;
        },
        (err: HttpErrorResponse) => {
          switch ((<HttpErrorResponse>err).status) {
            case 404:
              this.translate.get('RESERVATION_NOT_FOUND', { id: this.idReservation }).subscribe(
                (text: string) => {
                  this.messages.add(text, MessageType.Error);
                });
              break;
            default:
              this.translate.get('RESERVATION_LOAD_FAILED', { id: this.idReservation }).subscribe(
                (text: string) => {
                  this.messages.add(text, MessageType.Error);
                });
          }

          this.isLoading = false;
        });
  }

  /**
   * handle click on "Download calendar file"
   *
   * @memberof ConfirmReservationDialogComponent
   */
  onDownloadIcs() {
    this.isButtonLoading = true;
    this.reservationService.getIcsLink(this.idReservation).subscribe(
      (link: string) => {
        this.isButtonLoading = false;
        window.location.assign(link);
      },
      _ => {
        this.translate.get('RESERVATION_ICS_CREATION_FAILED').subscribe(
          (text: string) => {
            this.messages.add(text, MessageType.Error);
          });
        this.isButtonLoading = false;
      }
    );
  }

  initStatus() {
    switch (this.reservation.StatusId) {
      case StatusId.rProvAnfrage:
      case StatusId.rAbklärung:
        this.applyText('PENDING');
        break;
      case StatusId.rProvisorisch:
        this.applyText('PROVISIONAL');
        break;
      case StatusId.rBestätigt:
        this.applyText('CONFIRMED');
        break;
      default:
        console.error(`unknown state:  ${this.reservation.StatusId} - ${this.reservation.Status}`);
        console.log(this.reservation);
    }
  }

  applyText(state: string) {
    let prefix = '';
    switch (this.reservation.ResourceTypeId) {
      case ResourceType.Raum:
        prefix = 'ROOM';
        break;
      case ResourceType.Gerät:
        prefix = 'DEVICE';
        break;
      default:
        throw Error(`Ressourec of type "${this.reservation.ResourceTypeId}" not implemented`);
    }

    this.translate.get(prefix + '_CONFIRMATION_' + state + '_TITLE').subscribe((value: string) => {
      this.viewData.title = value;
    });
    this.translate.get(prefix + '_CONFIRMATION_' + state + '_MAINTEXT', { room: this.reservation.Resource }).subscribe((value: string) => {
      this.viewData.mainText = value;
    });
    this.translate.get(prefix + '_CONFIRMATION_' + state + '_SUBTEXT').subscribe((value: string) => {
      this.viewData.subText = value;
    });
  }

}

interface IViewData {
  title: string;
  mainText: string;
  subText: string;
}
